import { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { Button, FormControl, FormLabel, Grid, Input, Textarea, VStack } from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import { getCustomers, getSalesUsers } from 'api'
import { format as formatDate } from 'date-fns'

export const OpportunityFormType = {
  Add: 'add',
  Edit: 'edit'
}

export const OpportunityForm = ({ type, onSubmit, isSubmitting, initialForm = null }) => {
  const [form, setForm] = useImmer({
    salesUser: null,
    name: '',
    company: '',
    customer: null,
    email: '',
    phone: '',
    amount: 0,
    closingDate: null,
    stage: '',
    signType: [],
    probability: '',
    address: '',
    state: '',
    zip: '',
    campaignId: [],
    description: '',
    zohoId: ''
  })

  const buttonText = useMemo(() => {
    switch (type) {
      case OpportunityFormType.Add:
        return 'Add Opportunity'
      case OpportunityFormType.Edit:
        return 'Save Opportunity'
    }
  }, [type])

  useEffect(() => {
    setForm(draft => {
      Object.keys(initialForm || {}).forEach(k => { draft[k] = initialForm[k] ?? '' })
    })
  }, [initialForm])

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(form)
  }

  const closingDate = useMemo(() => {
    const date = new Date(form.closingDate)
    return form.closingDate ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'yyyy-MM-dd') : ''
  }, [form.closingDate])

  console.log(form)
  return (
    <form onSubmit={handleSubmit}>
      <Grid templateColumns="repeat(2, 1fr)">
        <VStack gap={2} maxW="500px">
          <FormControl>
            <FormLabel>Sales User</FormLabel>
            <UiSelect
              value={form.salesUser}
              onChange={v => {
                setForm(draft => { draft.salesUser = v })
              }}
              loadData={(page, search) => getSalesUsers(page, 20, search, 'name', 'asc')}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => v.name}
              placeholder="Search Users"
              noOptionsMessage={() => 'No Users Found'}
              required={true}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Name</FormLabel>
            <Input name="name" value={form.name} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Customer</FormLabel>
            <UiSelect
              value={form.customer}
              onChange={v => {
                setForm(draft => { draft.customer = v })
              }}
              loadData={(page, search) => getCustomers(page, 20, search, 'name', 'asc')}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => v.name}
              placeholder="Search Users"
              noOptionsMessage={() => 'No Users Found'}
              required={true}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input type="email" name="email" value={form.email} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Phone</FormLabel>
            <Input name="phone" value={form.phone} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Amount</FormLabel>
            <Input
              name="amount"
              type="number"
              value={form.amount}
              onChange={handleChange}
              onBlur={e => setForm(draft => { draft.amount = parseFloat(e.target.value || 0).toFixed(2) })}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Closing Date</FormLabel>
            <Input type="date" name="closingDate" value={closingDate} onChange={handleChange} />
          </FormControl>
        </VStack>
        <VStack gap={2} maxW="500px">
          <FormControl>
            <FormLabel>Stage</FormLabel>
            <Input name="stage" value={form.stage} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Sign Type (write types comma separated)</FormLabel>
            <Input name="signType" value={form.signType.join(',')} onChange={(e) => { setForm(draft => { draft.signType = e.target.value.split(',') || [] }) }} />
          </FormControl>
          <FormControl>
            <FormLabel>Probability</FormLabel>
            <Input name="probability" value={form.probability} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Address</FormLabel>
            <Input name="address" value={form.address} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>State</FormLabel>
            <Input name="state" value={form.state} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Zip</FormLabel>
            <Input name="zip" value={form.zip} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>Campaign ID (write types comma separated)</FormLabel>
            <Input name="campaignId" value={form.campaignId.join(',')} onChange={(e) => { setForm(draft => { draft.campaignId = e.target.value.split(',') || [] }) }} />
          </FormControl>
        </VStack>
      </Grid>
      <FormControl mt={4}>
        <FormLabel>Description</FormLabel>
        <Textarea rows={5} value={form.description} onChange={handleChange} />
      </FormControl>
      <Button type="submit" mt={8} colorScheme="blue" isLoading={isSubmitting}>{buttonText}</Button>
    </form>
  )
}

OpportunityForm.propTypes = {
  type: PropTypes.oneOf(Object.values(OpportunityFormType)).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  initialForm: PropTypes.object
}
